import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Message = ({ role, text }) => (
  <div className={`flex ${role === 'user' ? 'justify-end' : 'justify-start'} mb-3`}>
    <div className={`${role === 'user' ? 'bg-amber-500 text-black' : 'bg-neutral-900 text-white'} px-4 py-3 rounded-lg max-w-[80%] whitespace-pre-wrap break-words border ${role === 'user' ? 'border-amber-400' : 'border-neutral-800'}`}>
      {text}
    </div>
  </div>
);

export default function AiChatbot() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Describe the strategy you want. I will reply with a preset string you can paste into the Sandbox Pine script settings.' },
  ]);
  const [input, setInput] = useState('Fast-paced scalping on 5m using volatility breakout with quick exits.');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userText = input.trim();
    setMessages(prev => [...prev, { role: 'user', text: userText }]);
    setInput('');
    setLoading(true);
    try {
      const resp = await fetch('/api/presets/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: userText })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'Failed');
      const preset = data.preset || '';
      const reply = preset ? preset : (data.warning || 'No valid preset was generated. Try rephrasing with more detail.');
      setMessages(prev => [...prev, { role: 'assistant', text: reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', text: `Error: ${e.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  const copyLastPreset = async () => {
    const last = [...messages].reverse().find(m => m.role === 'assistant');
    if (last && last.text) {
      try { await navigator.clipboard.writeText(last.text); } catch {}
    }
  };

  const quick = (text) => setInput(text);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Nav */}
      <nav className="fixed top-0 w-full bg-black/95 backdrop-blur-xl border-b border-neutral-800 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-500 to-amber-700 rounded flex items-center justify-center" />
              <div>
                <div className="text-lg sm:text-xl font-bold tracking-tight">HDAlgos</div>
                <div className="text-[8px] sm:text-[9px] text-neutral-500 tracking-wider">INSTITUTIONAL SYSTEMS</div>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/system-library" className="text-sm text-neutral-400 hover:text-white transition">System Library</Link>
              <Link to="/ai-ml" className="text-sm text-neutral-400 hover:text-white transition">AI / Machine Learning</Link>
              <Link to="/checkout" className="bg-gradient-to-r from-amber-500 to-amber-600 text-black px-6 py-2 rounded-lg font-bold text-sm hover:from-amber-400 hover:to-amber-500 transition">GET STARTED</Link>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-neutral-400 hover:text-white transition" aria-label="Toggle menu">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/98 border-t border-neutral-800">
            <div className="px-4 py-4 space-y-3">
              <Link to="/system-library" className="block py-2 text-sm text-neutral-400 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>System Library</Link>
              <Link to="/ai-ml" className="block py-2 text-sm text-neutral-400 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>AI / Machine Learning</Link>
              <Link to="/checkout" className="block py-2 text-sm text-neutral-400 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
            </div>
          </div>
        )}
      </nav>

      <div className="max-w-5xl mx-auto px-4 pt-24 pb-16">
        <h1 className="text-3xl md:text-4xl font-black mb-2">AI Chatbot</h1>
        <p className="text-neutral-400 mb-6">Describe your strategy; get a valid preset string for the Sandbox Pine strategy.</p>

        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => quick('Double inside bar then break mother high within 3 bars; HTF align up; ATR stop 0.5; scalp 5m')} className="px-3 py-2 text-xs bg-neutral-900 border border-neutral-800 rounded-md hover:bg-neutral-800">Double inside → break high</button>
          <button onClick={() => quick('Donchian breakout length 20 with Daily ADX >= 18; trail ATR 1.0; keep both directions')} className="px-3 py-2 text-xs bg-neutral-900 border border-neutral-800 rounded-md hover:bg-neutral-800">Donchian + ADX</button>
          <button onClick={() => quick('MA cross 9/21 EMA with MA align (50 SMA) filter; fixed stop 1ATR TP 2ATR')} className="px-3 py-2 text-xs bg-neutral-900 border border-neutral-800 rounded-md hover:bg-neutral-800">MA Cross + MA Align</button>
        </div>

        <div ref={scrollRef} className="h-[50vh] md:h-[60vh] overflow-y-auto bg-neutral-950 border border-neutral-800 rounded-xl p-4 mb-3">
          {messages.map((m, i) => (<Message key={i} role={m.role} text={m.text} />))}
          {loading && (<div className="text-neutral-400 text-sm">Thinking…</div>)}
        </div>

        <div className="flex gap-2">
          <input
            className="flex-1 bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-3 text-white"
            placeholder="Describe your strategy…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          />
          <button onClick={send} disabled={loading} className="px-4 py-3 rounded-lg bg-amber-500 text-black font-bold disabled:opacity-50">Send</button>
          <button onClick={copyLastPreset} className="px-4 py-3 rounded-lg bg-neutral-900 border border-neutral-800">Copy Last</button>
        </div>

        <div className="text-neutral-500 text-xs mt-3">Output is a key=value;key2=value2 preset string. Paste it into the strategy settings under “Preset (LLM Overrides)”.</div>
      </div>
    </div>
  );
}
